import {
  shallowEqual, useDispatch, useSelector
} from 'react-redux';
import {
  useCallback,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import MusicPlayerDesktop from './Desktop';
import PlayerBarMobile from './Mobile';
import { EPISODE_KEY } from '@/config/routeKeys';
import { channelRecommendationTypes } from '@/config/events-keakie';
import { generateHref } from '@/helpers/routing';
import { goToChannel, goToEpisode } from '@/helpers/pages/channel';
import { share } from '@/helpers/share';
import { shouldCall } from '@/helpers/api';
import useAudioReduxControls from '@/hooks/audio/useAudioReduxControls';
import useAudioReduxControlsCustom from '@/hooks/audio/useAudioReduxControlsCustom';
import useCallApiSimple from '@/hooks/api/useCallApiSimple';
import useLibraryUserEdge from '@/hooks/api/useLibraryUserEdge';
import useLiveAudioVolume from '@/hooks/audio/useLiveAudioVolume';
import useShouldRenderForMobile from '@/hooks/layout/useShouldRenderForMobile';
import useUser from '@/hooks/api/useUser';
import {
  selectAudioMuted,
  selectAudioPaused,
  selectAudioReadyForSeek,
  selectAudioVolume,
  selectCurrentEpisode,
  selectCurrentEpisodeId,
  selectCurrentEpisodeNumber,
  selectCurrentEpisodeSeason,
  selectCurrentEpisodeStatus,
  selectCurrentShow,
  selectCustomQueueCount,
  selectPlayPauseIsAvailable,
  selectPreviousTrackIsAvailable,
  selectNextTrackIsAvailable
} from '@/state/selectors/audio';
import { selectUserEpisodeEdges } from '@/state/selectors/user';
import { addToLibrary, removeFromLibrary } from '@/state/library/actions';
import { getEpisode, shiftEpisodeInQueue } from '@/state/audio/actions';

const DEFAULT_OBJECT = {};

const selector = (state) => ({
  paused: selectAudioPaused(state),
  volume: selectAudioVolume(state),
  disablePlayPause: !selectPlayPauseIsAvailable(state),
  disablePrevious: !selectPreviousTrackIsAvailable(state),
  disableNext: !selectNextTrackIsAvailable(state),
  muted: selectAudioMuted(state),
  currentlyPlayingEpisodeId: selectCurrentEpisodeId(state),
  currentlyPlayingEpisode: selectCurrentEpisode(state),
  currentlyPlayingEpisodeStatus: selectCurrentEpisodeStatus(state),
  currentlyPlayingEpisodeSeason: selectCurrentEpisodeSeason(state),
  currentlyPlayingEpisodeNumber: selectCurrentEpisodeNumber(state),
  currentShow: selectCurrentShow(state),
  audioReadyForSeek: selectAudioReadyForSeek(state),
  customQueueCount: selectCustomQueueCount(state),
  episodeEdges: selectUserEpisodeEdges(state)
});

const MusicPlayerBar = ({ isFiltered }) => {
  const dispatch = useDispatch();
  const { isLoggedInWithProvider } = useUser();
  const {
    paused,
    volume,
    muted,
    disablePlayPause,
    disablePrevious,
    disableNext,
    currentlyPlayingEpisodeId,
    currentlyPlayingEpisode,
    currentlyPlayingEpisodeStatus,
    currentlyPlayingEpisodeSeason,
    currentlyPlayingEpisodeNumber,
    currentShow,
    customQueueCount,
    episodeEdges
  } = useSelector(selector, shallowEqual);
  const {
    handleChangeVolume, handleSaveVolume, handleToggleMute
  } = useLiveAudioVolume();
  const { handlePlayOrPause } = useAudioReduxControls();
  const {
    handlePreviousTrack,
    handleNextTrack
  } = useAudioReduxControlsCustom();

  const shouldRenderForMobile = useShouldRenderForMobile();

  const handleMoveEpisode = useCallback(({
    from,
    to
  }) => {
    dispatch(shiftEpisodeInQueue({
      from,
      to
    }));
  },[dispatch, currentlyPlayingEpisodeNumber, currentShow.slug, currentlyPlayingEpisodeSeason, currentlyPlayingEpisodeId]);

  const episode = currentlyPlayingEpisode || DEFAULT_OBJECT;

  const handleFetchEpisode = useCallback(() => dispatch(getEpisode({
    episodeNumber: currentlyPlayingEpisodeNumber,
    channelSlug: currentShow.slug,
    season: currentlyPlayingEpisodeSeason,
    episodeId: currentlyPlayingEpisodeId
  })), [dispatch, currentlyPlayingEpisodeNumber, currentShow.slug, currentlyPlayingEpisodeSeason, currentlyPlayingEpisodeId]);

  useCallApiSimple({
    key: currentlyPlayingEpisodeId,
    shouldCall: currentlyPlayingEpisodeId && shouldCall({ status: currentlyPlayingEpisodeStatus }).should,
    call: handleFetchEpisode,
    shouldRetry: true,
    hasError: currentlyPlayingEpisodeStatus.error,
    infiniteRetry: true
  });

  const { edge, edgeStatus } = useLibraryUserEdge({
    episodeId: currentlyPlayingEpisodeId,
    episodeEdges
  });

  const queueAndLibraryParams = useMemo(() => ({
    id: currentlyPlayingEpisodeId,
    title: episode.title,
    assets: currentShow.assets,
    episode
  }), [currentShow.assets, episode, currentlyPlayingEpisodeId]);

  const { userStatus } = useUser();

  const isOpen = !!userStatus.success && !!(currentlyPlayingEpisodeId || !!customQueueCount);

  const handleAddToLibrary = useCallback(() => {
    dispatch(addToLibrary(queueAndLibraryParams));
  }, [dispatch, queueAndLibraryParams]);

  const handleRemoveFromLibrary = useCallback(() => {
    dispatch(removeFromLibrary(queueAndLibraryParams));
  }, [dispatch, queueAndLibraryParams]);

  let onClickLibrary;
  let libraryActionType = 'add';

  if (edgeStatus.loading || edgeStatus.success) {
    libraryActionType = edge.in_library ? 'remove' : 'add';

    if (isLoggedInWithProvider) {
      onClickLibrary = edge.in_library ? handleRemoveFromLibrary : handleAddToLibrary;
    }
  }

  const onClickChannel = useCallback(() => {
    goToChannel({
      channelId: currentShow.id,
      channelSlug: currentShow.slug,
      recommendationType: channelRecommendationTypes.EPISODE,
      recommendationId: null
    });
  }, [currentShow.id, currentShow.slug]);

  const onClickEpisode = useCallback(() => {
    goToEpisode({
      channelId: currentShow.id,
      channelSlug: currentShow.slug,
      episodeNumber: currentlyPlayingEpisodeNumber,
      seasonNumber: currentlyPlayingEpisodeSeason,
      recommendationType: channelRecommendationTypes.EPISODE,
      recommendationId: null
    });
  }, [currentShow.id, currentShow.slug, currentlyPlayingEpisodeNumber, currentlyPlayingEpisodeSeason]);

  const handleShare = useCallback(() => {
    const shareUrlParams = {
      channel_slug: currentShow.slug,
      episode_number: currentlyPlayingEpisodeNumber,
      season: currentlyPlayingEpisodeSeason
    };

    share({
      url: generateHref(EPISODE_KEY, shareUrlParams),
      tooltipText: 'Copied to clipboard' // Replace with the desired tooltip text
    });
  }, [currentlyPlayingEpisodeNumber, currentlyPlayingEpisodeSeason, currentShow.slug]);

  return shouldRenderForMobile ? (
    <PlayerBarMobile
      disablePlayPause={ disablePlayPause }
      episode={ episode }
      isFiltered={ isFiltered }
      isOpen={ isOpen }
      onMoveEpisode={ handleMoveEpisode }
      onPlayOrPause={ disablePlayPause ? undefined : handlePlayOrPause }
      paused={ paused }
    />
  ) : (
    <MusicPlayerDesktop
      airplayAvailable={ false }
      disableNext={ disableNext }
      disablePrevious={ disablePrevious }
      episode={ episode }
      isFiltered={isFiltered}
      isOpen={isOpen}
      libraryActionLoading={!!edgeStatus.loading}
      libraryActionType={libraryActionType}
      libraryLoading={!!edgeStatus.loading}
      muted={muted}
      onChangeVolume={handleChangeVolume} // Temporary until reintroducing airplay
      onClickAirplay={undefined} // Temporary until reintroducing airplay
      onClickChannel={onClickChannel}
      onClickEpisode={onClickEpisode}
      onClickLibrary={onClickLibrary}
      onMoveEpisode={handleMoveEpisode}
      onNext={handleNextTrack}
      onPlayOrPause={disablePlayPause ? undefined : handlePlayOrPause}
      onPrevious={handlePreviousTrack}
      onSetMuted={handleToggleMute}
      onSetVolume={handleSaveVolume}
      onShare={handleShare}
      paused={paused}
      volume={volume}
    />
  );
};

MusicPlayerBar.propTypes = { isFiltered: PropTypes.bool };

MusicPlayerBar.defaultProps = { isFiltered: false };

export default MusicPlayerBar;
